import { state, currentUser, currentUserRole } from "../state";
import { apiCall } from "../api";
import { showToast, opDisplayNames } from "../utils";

export async function loadAdminOverrides(): Promise<void> {
  const data = await apiCall("/api/strategies/overrides", "GET");
  state.adminOverrides = data.strategies || {};
}

export async function loadUserStrategies(): Promise<void> {
  const token = localStorage.getItem("token");
  if (!token) return;
  const data = await apiCall("/api/strategies/user", "GET", null, false, token);
  if (data && data.strategies && Object.keys(data.strategies).length) {
    state.strategies = data.strategies;
  } else {
    await loadDefaultStrategies();
  }
  renderStrategyEditor();
}

export async function loadDefaultStrategies(): Promise<void> {
  const data = await apiCall("/api/strategies/default", "GET");
  state.strategies = data.strategies || {};
  renderStrategyEditor();
}

export async function loadStrategyGroups(): Promise<void> {
  const data = await apiCall("/api/strategies/all_groups", "GET");
  if (data && data.groups) {
    const select = document.getElementById("strat-group-select") as HTMLSelectElement;
    select.innerHTML = data.groups.map((g: any) => `<option value="${g.value}">${g.label}</option>`).join("");
    if (data.groups.length && !select.value) {
      select.value = data.groups[0].value;
    }
    renderStrategyEditor();
  }
}

export async function renderStrategyEditor(): Promise<void> {
  const group = (document.getElementById("strat-group-select") as HTMLSelectElement)?.value;
  if (!group) return;
  const data = await apiCall(`/api/attributes/group/${encodeURIComponent(group)}`, "GET");
  const groupAttrs = data.attributes || [];
  const editorDiv = document.getElementById("strat-editor");
  if (!editorDiv) return;
  if (!groupAttrs.length) {
    editorDiv.innerHTML = '<div class="warning">Нет атрибутов для этой группы</div>';
    return;
  }
  const mode = (document.getElementById("strat-mode") as HTMLSelectElement).value;
  let displayConfig: any = {};
  if (currentUserRole === "admin" && mode !== "custom") {
    displayConfig = state.adminOverrides[group]?.[mode] || {};
  } else {
    const currentStrat = state.strategies[group] || { mode: "custom", config: {} };
    displayConfig = currentStrat.config || {};
  }
  let html = '<div style="max-height:400px; overflow-y:auto;"><table style="width:100%"><thead><tr><th>Атрибут</th><th>Использовать</th><th>Оператор / Настройки</th></tr></thead><tbody>';
  for (const attr of groupAttrs) {
    const key = attr.key;
    const label = attr.label;
    const attrType = attr.type || "categorical";
    const isRequired = attr.is_required || false;
    let currentCfg = displayConfig[key];
    if (typeof currentCfg === "string") {
      currentCfg = { op: currentCfg };
    } else if (!currentCfg) {
      currentCfg = { op: "=" };
    }
    const currentOp = currentCfg.op || "=";
    const checked = displayConfig[key] ? "checked" : "";
    let opOptions: string[] = [];
    if (attrType === "numeric") {
      opOptions = ["=", ">=", "<=", "between", "pct_range"];
    } else {
      opOptions = ["=", "in"];
    }
    let subHtml = "";
    if (currentOp === "in") {
      const m = currentCfg.mode || "overlap";
      subHtml = `<select data-attr-mode="${key}" style="margin-left:8px;">
        <option value="overlap" ${m === "overlap" ? "selected" : ""}>${opDisplayNames["overlap"]}</option>
        <option value="contains_all" ${m === "contains_all" ? "selected" : ""}>${opDisplayNames["contains_all"]}</option>
      </select>`;
    } else if (currentOp === "between") {
      const a = currentCfg.a !== undefined ? currentCfg.a : "";
      const b = currentCfg.b !== undefined ? currentCfg.b : "";
      subHtml = `<span style="margin-left:8px;">от <input type="number" data-attr-a="${key}" value="${a}" style="width:80px;"></span>
                 <span>до <input type="number" data-attr-b="${key}" value="${b}" style="width:80px;"></span>`;
    } else if (currentOp === "pct_range") {
      const x = currentCfg.x !== undefined ? currentCfg.x : 0;
      const y = currentCfg.y !== undefined ? currentCfg.y : 0;
      subHtml = `<span style="margin-left:8px;">+<input type="number" data-attr-x="${key}" value="${x}" style="width:60px;">%
                 /-<input type="number" data-attr-y="${key}" value="${y}" style="width:60px;">%</span>`;
    }
    html += `
      <tr>
        <td>${label}</td>
        <td><input type="checkbox" data-attr="${key}" ${checked} ${isRequired ? "disabled" : ""}></td>
        <td>
          <select data-attr-op="${key}" ${!checked ? "disabled" : ""} data-attr-type="${attrType}">
            ${opOptions.map((op: string) => `<option value="${op}" ${currentOp === op ? "selected" : ""}>${opDisplayNames[op] || op}</option>`).join("")}
          </select>
          <span data-attr-sub="${key}">${subHtml}</span>
        </td>
      </tr>`;
  }
  html += "</tbody></table></div>";
  editorDiv.innerHTML = html;

  editorDiv.querySelectorAll('select[data-attr-op]').forEach((sel: Element) => {
    sel.addEventListener("change", (e) => {
      const target = e.target as HTMLSelectElement;
      const key = target.dataset.attrOp!;
      const attrType = target.dataset.attrType || "categorical";
      onStrategyOpChange(target, key, attrType);
    });
  });
  editorDiv.querySelectorAll('input[type="checkbox"]').forEach((cb: Element) => {
    cb.addEventListener("change", (e) => {
      const target = e.target as HTMLInputElement;
      const attr = target.dataset.attr;
      const select = document.querySelector(`#strat-editor select[data-attr-op="${attr}"]`) as HTMLSelectElement | null;
      if (select) select.disabled = !target.checked;
      updatePreview();
    });
  });
  updatePreview();
}

export function onStrategyOpChange(selectEl: HTMLSelectElement, key: string, attrType: string): void {
  const op = selectEl.value;
  const container = document.querySelector(`#strat-editor span[data-attr-sub="${key}"]`);
  if (!container) return;
  let html = "";
  if (op === "in") {
    html = `<select data-attr-mode="${key}" style="margin-left:8px;">
      <option value="overlap" selected>${opDisplayNames["overlap"]}</option>
      <option value="contains_all">${opDisplayNames["contains_all"]}</option>
    </select>`;
  } else if (op === "between") {
    html = `<span style="margin-left:8px;">от <input type="number" data-attr-a="${key}" value="" style="width:80px;"></span>
            <span>до <input type="number" data-attr-b="${key}" value="" style="width:80px;"></span>`;
  } else if (op === "pct_range") {
    html = `<span style="margin-left:8px;">+<input type="number" data-attr-x="${key}" value="0" style="width:60px;">%
            /-<input type="number" data-attr-y="${key}" value="0" style="width:60px;">%</span>`;
  }
  container.innerHTML = html;
  updatePreview();
}

export function collectStrategyConfig(): { mode: string; config: any } {
  const group = (document.getElementById("strat-group-select") as HTMLSelectElement).value;
  const mode = (document.getElementById("strat-mode") as HTMLSelectElement).value;
  const config: any = {};
  const rows = document.querySelectorAll("#strat-editor tbody tr");
  for (const row of rows) {
    const cb = row.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
    if (cb && cb.checked) {
      const attr = cb.dataset.attr;
      const select = row.querySelector("select[data-attr-op]") as HTMLSelectElement | null;
      const op = select ? select.value : "=";
      const cfg: any = { op };
      if (op === "in") {
        const modeSel = row.querySelector(`select[data-attr-mode="${attr}"]`) as HTMLSelectElement | null;
        cfg.mode = modeSel ? modeSel.value : "overlap";
      } else if (op === "between") {
        const aInput = row.querySelector(`input[data-attr-a="${attr}"]`) as HTMLInputElement | null;
        const bInput = row.querySelector(`input[data-attr-b="${attr}"]`) as HTMLInputElement | null;
        if (aInput && aInput.value !== "") cfg.a = parseFloat(aInput.value);
        if (bInput && bInput.value !== "") cfg.b = parseFloat(bInput.value);
      } else if (op === "pct_range") {
        const xInput = row.querySelector(`input[data-attr-x="${attr}"]`) as HTMLInputElement | null;
        const yInput = row.querySelector(`input[data-attr-y="${attr}"]`) as HTMLInputElement | null;
        cfg.x = xInput ? parseFloat(xInput.value) || 0 : 0;
        cfg.y = yInput ? parseFloat(yInput.value) || 0 : 0;
      }
      config[attr] = cfg;
    }
  }
  return { mode, config };
}

export function updatePreview(): void {
  const { config } = collectStrategyConfig();
  const preview = document.getElementById("strat-preview");
  if (preview) preview.innerHTML = `<pre>${JSON.stringify(config, null, 2)}</pre>`;
}

export async function saveCurrentStrategy(): Promise<void> {
  const group = (document.getElementById("strat-group-select") as HTMLSelectElement).value;
  if (!group) return;
  const { mode, config } = collectStrategyConfig();
  if (currentUserRole === "admin" && mode !== "custom") {
    state.adminOverrides[group] = state.adminOverrides[group] || {};
    state.adminOverrides[group][mode] = config;
    if (currentUser) {
      const token = localStorage.getItem("token");
      await apiCall(`/api/admin/strategies/${group}/${mode}`, "POST", { mode, config }, false, token);
      showToast(`Глобальная стратегия "${mode}" для группы "${group}" сохранена`, "success");
    } else {
      showToast("Стратегия сохранена локально", "info");
    }
  } else {
    state.strategies[group] = { mode, config };
    if (currentUser) {
      await apiCall("/api/strategies/user/save", "POST", { strategies: state.strategies });
      showToast(`Стратегия для группы "${group}" сохранена на сервере`, "success");
    } else {
      showToast("Стратегия сохранена локально (войдите для сохранения на сервере)", "info");
    }
  }
  updatePreview();
}

export async function resetGroupStrategy(): Promise<void> {
  const group = (document.getElementById("strat-group-select") as HTMLSelectElement).value;
  if (!group) return;
  const mode = (document.getElementById("strat-mode") as HTMLSelectElement).value;
  const defaultData = await apiCall("/api/strategies/default", "GET");
  if (currentUserRole === "admin" && mode !== "custom") {
    const defaultStrat = defaultData.strategies?.[group];
    const defaultConfig = defaultStrat?.config || {};
    state.adminOverrides[group] = state.adminOverrides[group] || {};
    state.adminOverrides[group][mode] = defaultConfig;
    if (currentUser) {
      const token = localStorage.getItem("token");
      await apiCall(`/api/admin/strategies/${group}/${mode}`, "POST", { mode, config: defaultConfig }, false, token);
    }
    renderStrategyEditor();
    showToast(`Глобальная стратегия "${mode}" для "${group}" сброшена к дефолтной`, "success");
  } else {
    const defaultConfig = defaultData.strategies?.[group]?.config || {};
    state.strategies[group] = { mode: "custom", config: defaultConfig };
    if (currentUser) {
      await apiCall("/api/strategies/user/save", "POST", { strategies: state.strategies });
    }
    renderStrategyEditor();
    showToast(`Стратегия для "${group}" сброшена к дефолтной`, "success");
  }
}

export async function resetAllStrategies(): Promise<void> {
  await loadDefaultStrategies();
  if (currentUser) {
    await apiCall("/api/strategies/user/save", "POST", { strategies: state.strategies });
  }
  renderStrategyEditor();
  showToast("Все стратегии сброшены к дефолтным", "success");
}

export async function loadDefaultStrategiesAndApply(): Promise<void> {
  await loadDefaultStrategies();
  showToast("Загружены базовые стратегии (по умолчанию)", "success");
}

export function initStrategies(): void {
  const stratGroup = document.getElementById("strat-group-select");
  if (stratGroup) stratGroup.addEventListener("change", () => renderStrategyEditor());

  const stratMode = document.getElementById("strat-mode");
  if (stratMode) {
    stratMode.addEventListener("change", async () => {
      const group = (document.getElementById("strat-group-select") as HTMLSelectElement).value;
      const mode = (document.getElementById("strat-mode") as HTMLSelectElement).value;
      if (mode !== "custom") {
        if (currentUserRole === "admin") {
          const override = state.adminOverrides[group]?.[mode];
          if (!override) {
            const res = await apiCall(`/api/strategies/preset/${group}/${mode}`, "GET");
            if (res && res.config) {
              state.adminOverrides[group] = state.adminOverrides[group] || {};
              state.adminOverrides[group][mode] = res.config;
            }
          }
          renderStrategyEditor();
        } else {
          const res = await apiCall(`/api/strategies/preset/${group}/${mode}`, "GET");
          if (res && res.config) {
            state.strategies[group] = { mode, config: res.config };
            renderStrategyEditor();
          }
        }
      } else {
        if (!state.strategies[group]) state.strategies[group] = { mode: "custom", config: {} };
        state.strategies[group].mode = "custom";
        renderStrategyEditor();
      }
    });
  }

  const stratSave = document.getElementById("strat-save");
  if (stratSave) stratSave.addEventListener("click", () => saveCurrentStrategy());

  const stratResetGroup = document.getElementById("strat-reset-group");
  if (stratResetGroup) stratResetGroup.addEventListener("click", () => resetGroupStrategy());

  const stratResetAll = document.getElementById("strat-reset-all");
  if (stratResetAll) stratResetAll.addEventListener("click", () => resetAllStrategies());

  const useDefault = document.getElementById("use-default-strategies");
  if (useDefault) useDefault.addEventListener("click", () => loadDefaultStrategiesAndApply());
}
